import { useEffect, useState } from "react";
import { Activity } from "lucide-react";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { PageHeader, Card, EmptyState } from "@/components/ranger/PageHeader";
import { StatusBadge } from "@/components/ranger/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { api, fmtTime } from "@/lib/api";

export default function Analytics() {
  const { active } = useAuth();
  const [data, setData] = useState(null);
  const [hours, setHours] = useState(24);
  useEffect(() => { api.get(`/communities/${active.id}/health/history?hours=${hours}`).then((r) => setData(r.data)); }, [active.id, hours]);
  if (!data) return null;
  const chart = [...data.checks].reverse().filter((c) => c.ok).map((c) => ({ t: new Date(c.created_at).toLocaleTimeString(), ms: c.latency_ms }));
  const s = data.summary;

  return (
    <div>
      <PageHeader eyebrow="Recorded telemetry only" title="Analytics" description="Connection health from real RCON checks. Gameplay analytics arrive in Phase 4."
        actions={[24, 168].map((h) => <button key={h} data-testid={`analytics-range-${h}`} onClick={() => setHours(h)} className={`rounded-sm px-3 py-1.5 text-xs font-semibold uppercase tracking-wider ${hours === h ? "bg-[#E5202E]/15 text-[#E5202E]" : "text-slate-400"}`}>{h === 24 ? "24h" : "7d"}</button>)} />
      {!s.total ? (
        <EmptyState icon={Activity} title="No health checks recorded" testId="analytics-empty">Checks run every minute once RCON is connected, and on every manual connection test.</EmptyState>
      ) : (
        <div className="grid gap-5 lg:grid-cols-[1fr_2fr]">
          <Card title="Summary">
            <dl className="grid grid-cols-2 gap-4">
              {[["Uptime", `${s.uptime_pct}%`], ["Checks", s.total], ["Passed", s.ok], ["Avg latency", s.avg_latency_ms != null ? `${s.avg_latency_ms}ms` : "—"]].map(([k, v]) => (
                <div key={k}><dt className="rg-label">{k}</dt><dd data-testid={`analytics-${k.toLowerCase().replace(" ", "-")}`} className="font-display text-3xl font-bold text-slate-100">{v}</dd></div>
              ))}
            </dl>
          </Card>
          <Card title="Latency (ms)">
            <div className="h-56">
              <ResponsiveContainer><BarChart data={chart}><XAxis dataKey="t" stroke="#475569" fontSize={10} /><YAxis stroke="#475569" fontSize={10} /><Tooltip contentStyle={{ background: "#0B1120", border: "1px solid #1E2D4A" }} /><Bar dataKey="ms" fill="#E5202E" /></BarChart></ResponsiveContainer>
            </div>
          </Card>
          <Card title="Recent checks" className="lg:col-span-2">
            <ul className="max-h-80 divide-y divide-[#1E2D4A] overflow-y-auto">
              {data.checks.slice(0, 100).map((c) => (
                <li key={c.created_at} className="flex flex-wrap items-center gap-3 py-2 font-mono text-xs">
                  <StatusBadge state={c.ok ? "success" : "failure"} label={c.ok ? "pass" : "fail"} />
                  <span className="text-slate-500">{fmtTime(c.created_at)}</span>
                  <span className="text-slate-400">{c.source}</span>
                  {c.latency_ms != null && <span className="text-slate-300">{c.latency_ms}ms</span>}
                  {c.error && <span className="break-all text-red-400">{c.error}</span>}
                </li>
              ))}
            </ul>
          </Card>
        </div>
      )}
    </div>
  );
}
